import { inferRouteObject } from 'react-router-typed-object';
import { z } from 'zod';

import { Navigate } from '@/js/libs/router';
import { PrivateRoute, Roles } from '@/js/libs/use-auth';

import { CompanyDetailsTab, CompanyFormSearchKey } from 'src/scenes/Administration/containers/Companies/index.ts';
import Administration from 'src/scenes/Administration/index.ts';
import Status from 'src/scenes/Status/index.ts';

const index = true;

export const adminRouteObject = inferRouteObject({
  path: 'admin',
  element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
  children: [
    {
      Component: Administration.Helmet,
      children: [
        {
          path: 'users',
          children: [
            { index, Component: Administration.Users.UsersList },
            {
              path: 'create',
              Component: Administration.Users.UserForm,
            },
            {
              path: 'edit',
              children: [
                { index, element: <Navigate to='../' replace /> },
                {
                  path: ':userId',
                  Component: Administration.Users.UserForm,
                },
              ],
            },
          ],
        },
        {
          path: 'companies',
          children: [
            { index, Component: Administration.Companies.CompaniesList },
            {
              path: ':companyId',
              children: [
                {
                  Component: Administration.Companies.CompanyDetails.Layout,
                  children: [
                    {
                      index,
                      element: <Navigate to={`./${CompanyDetailsTab.WEBSITES}`} replace />,
                    },
                    {
                      path: CompanyDetailsTab.WEBSITES,
                      Component: Administration.Companies.CompanyDetails.WebsitesList,
                    },
                    {
                      path: CompanyDetailsTab.ACCOUNTS,
                      Component: Administration.Companies.CompanyDetails.AccountsList,
                    },
                    {
                      path: CompanyDetailsTab.PROGRAMMATIC_SEATS,
                      Component: Administration.Companies.CompanyDetails.ProgrammaticSeatsList,
                    },
                    {
                      path: CompanyDetailsTab.EMAIL_SYNC,
                      element: <PrivateRoute roles={Roles.EMAIL_SYNC} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          index,
                          Component: Administration.Companies.CompanyDetails.EmailSyncExclusionsList,
                        },
                      ],
                    },
                    {
                      path: CompanyDetailsTab.USER_GROUPS,
                      Component: Administration.Companies.CompanyDetails.UserGroupsList,
                    },
                    {
                      path: CompanyDetailsTab.PERMISSIONS,
                      Component: Administration.Companies.CompanyDetails.PermissionsList,
                    },
                  ],
                },
              ],
            },
            {
              path: 'edit',
              children: [
                {
                  path: ':companyId',
                  Component: Administration.Companies.CompanyForm,
                  searchParams: z.object({
                    [CompanyFormSearchKey.COMPANY_DETAILS_TAB]: z.string().optional(),
                  }).parse,
                },
                { index, element: <Navigate to='/admin/companies' replace /> },
                { path: '*', element: <Navigate to='/admin/companies' replace /> },
              ],
            },
          ],
        },
        {
          path: 'website-groups',
          children: [
            { index, Component: Administration.WebsiteGroups.WebsiteGroupsList },
            {
              path: 'create',
              Component: Administration.WebsiteGroups.WebsiteGroupForm,
            },
            {
              path: 'edit',
              children: [
                {
                  path: ':websiteGroupId',
                  Component: Administration.WebsiteGroups.WebsiteGroupForm,
                },
                { index, element: <Navigate to='/admin/website-groups' replace /> },
              ],
            },
          ],
        },
        { path: 'localization', Component: Administration.Localization },
        { path: 'website-mapping', Component: Administration.WebsiteMapping },
        {
          path: 'batch-k8s',
          children: [
            { index, path: 'list', Component: Administration.BatchK8s },
            { index, element: <Navigate to='/admin/batch-k8s/list' replace /> },
            { path: '*', element: <Navigate to='/admin/batch-k8s/list' replace /> },
          ],
        },
        {
          path: 'batch',
          Component: Administration.BatchLayout,
          children: [
            { path: 'list', Component: Administration.JobList },
            { path: 'log/:jobId/:resultId', Component: Administration.JobExecutionLog },
            { index, element: <Navigate to='/admin/batch/list' replace /> },
            { path: '*', element: <Navigate to='/admin/batch/list' replace /> },
          ],
        },
        {
          path: 'mail',
          children: [
            {
              Component: Administration.Mail.Layout,
              children: [
                { path: 'console', Component: Administration.Mail.Console },
                { path: 'sales-promotions', Component: Administration.Mail.SalesPromotions },
              ],
            },
            { index, element: <Navigate to='./console' replace /> },
          ],
        },
        { path: 'notifications', Component: Administration.Notifications.SystemNotificationsConfiguration },
        { path: 'xandr-credentials', Component: Administration.XandrCredentials },
        { path: 'permalead', Component: Administration.Permalead },
        { path: 'xandr-segments', Component: Administration.XandrSegments },
        { index, element: <Navigate to='/admin/users' replace /> },
      ],
    },
  ],
});
